import { DropDownList, ListItemProps } from '@progress/kendo-react-dropdowns';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { EditableColumnCategories } from 'src/assets/static/view_checkbook/useCheckbookCategories';
import Button from 'src/components/design-system/Button';
import { Keys } from 'src/constants/keys';
import useBoolean from 'src/hooks/useBoolean';
import { stopPropagation } from 'src/utils';

interface ColumnEditorDropDownProps {
  label?: string;
  value?: string;
  data: EditableColumnCategories[];
  onChange: (dataItem: EditableColumnCategories[]) => void;
}
const ColumnEditorDropDown: FC<ColumnEditorDropDownProps> = ({ data, onChange, label, value: allSelectedLabel }) => {
  const dropDownRef = useRef<DropDownList>(null);
  const [column, setColumn] = useState<EditableColumnCategories[]>(data);
  const { t } = useTranslation();
  const isAllSelected = useMemo(
    () => column.filter((c) => c.display).every((item) => item.show),
    [column]
  );
  const { value: open, setTrue: openDropdown, setFalse: closeDropdown } = useBoolean();
  const selectedCount = useMemo(() => column.filter((c) => c.show).length, [column]);
  const selectedVisibleItems = useMemo(
    () => column.filter((c) => c.display && c.show),
    [column]
  );
  const hasCustomLabel = Boolean(label && allSelectedLabel);
  const value = useMemo(() => {
    if (!hasCustomLabel) {
      return isAllSelected ? 'All Columns' : 'Custom Columns';
    }

    if (isAllSelected) {
      return allSelectedLabel;
    }

    if (selectedVisibleItems.length === 1) {
      return selectedVisibleItems[0].title;
    }

    if (selectedVisibleItems.length > 1) {
      return `${selectedVisibleItems.length} ${t('Supplier Type Selected')}`;
    }

    return allSelectedLabel;
  }, [hasCustomLabel, isAllSelected, allSelectedLabel, selectedVisibleItems, t]);

  useEffect(() => {
    setColumn(data);
  }, [data]);

  const handleSelectAll = useCallback(
    (event: CheckboxChangeEvent) => {
      setColumn((prev) =>
        prev.map((item) => ({ ...item, show: item.display ? event.value : item.show }))
      );
    },
    [setColumn]
  );

  const onCheckBoxChange = useCallback(
    (field: string) => {
      const newEditColumns = column.map((column) => {
        if (column.field === field) {
          return { ...column, show: !column.show };
        }
        return column;
      });
      setColumn(newEditColumns);
    },
    [column]
  );

  const handleLabelClick = useCallback(
    (event: MouseEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const field = (event.target as HTMLLabelElement).htmlFor;
      onCheckBoxChange(field);
    },
    [onCheckBoxChange]
  );

  const handleClose = useCallback(() => {
    setColumn(data);
    closeDropdown();
  }, [closeDropdown, data]);

  const handleOpen = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      openDropdown();
    }
  }, [handleClose, open, openDropdown]);

  const handleChange = useCallback(() => {
    onChange(column);
    closeDropdown();
  }, [closeDropdown, column, onChange]);

  const itemRender = useCallback(
    (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
      const { dataItem } = itemProps;
      const itemChildren = (
        <li onClick={stopPropagation} className="k-list-item text-[13px]">
          <Checkbox
            type="checkbox"
            id={dataItem.field}
            name={dataItem.field}
            checked={dataItem.show}
            onChange={() => {
              onCheckBoxChange(dataItem.field);
            }}
          >
            <label
              htmlFor={dataItem.field}
              className={'k-checkbox-label'}
              onClick={handleLabelClick}
            >
              {dataItem.title}
            </label>
          </Checkbox>
        </li>
      );

      return dataItem.display ? itemChildren : <></>;
    },
    [handleLabelClick, onCheckBoxChange]
  );

  const header = useMemo(
    () => (
      <li onClick={stopPropagation} className="k-list-item text-[13px] p-2">
        <Checkbox
          type="checkbox"
          checked={isAllSelected}
          onChange={handleSelectAll}
          label={label ? label : 'Select All'}
          autoFocus
        />
      </li>
    ),
    [handleSelectAll, isAllSelected,label]
  );
  const footer = useMemo(
    () => (
      <div onClick={stopPropagation} className="flex item-center justify-end gap-1">
        <Button onClick={handleChange} size="md" color="primary" disabled={selectedCount === 0}>
          Apply
        </Button>
        <Button onClick={handleClose} size="md" color="error">
          Cancel
        </Button>
      </div>
    ),
    [handleChange, handleClose,selectedCount]
  );

  const handleClickOutside = useCallback(
    (event: any) => {
      const popupRef = document.getElementsByClassName('columnEditorPopup')[0];
      if (popupRef && !popupRef.contains(event.target)) {
        if (dropDownRef.current?.element && !dropDownRef.current.element.contains(event.target)) {
          handleClose();
        }
      }
    },
    [handleClose]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleTabKeyPress = (event: KeyboardEvent<HTMLSpanElement>) => {
    // when user press tab key trap focus
    const modalElement = document.getElementsByClassName('columnEditorPopup')[0] as HTMLElement;
    if (!modalElement) return;
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.code === Keys.tab) {
        handleTabKeyPress(event);
      } else if (event.code === Keys.space) {
        openDropdown();
      } else if (event.code === Keys.enter) {
        handleOpen();
      }
      if (event.key === Keys.esc) {
        handleClose();
      }
    },
    [handleClose, handleOpen, openDropdown]
  );

  return (
    <span onClick={handleOpen} className="contents" onKeyDown={handleKeyDown}>
      <DropDownList
        id="view-drop-down-list"
        className="columnEditorDropdown"
        style={{ width: 200 }}
        data={column}
        itemRender={itemRender}
        header={header}
        footer={footer}
        value={value}
        opened={open}
        popupSettings={{ className: 'columnEditorPopup toolbar-dropdown-popup' }}
        ref={dropDownRef}
      />
    </span>
  );
};

export default ColumnEditorDropDown;
